import { WorkoutBlock, ProgramExercise } from '../types/program';

/**
 * Pre-made Warmup and Recovery/Cool-Down blocks for various workout types
 * These can be quickly inserted into custom programs
 */

export interface BlockTemplate {
  id: string;
  name: string;
  description: string;
  workoutType: 'cardio' | 'ruck' | 'strength-upper' | 'strength-lower' | 'strength' | 'swim';
  blockType: 'warm-up' | 'cool-down';
  block: Omit<WorkoutBlock, 'id'> & { type: 'prep' | 'recovery' };
}

export const WARMUP_TEMPLATES: BlockTemplate[] = [
  // CARDIO WARMUP
  {
    id: 'cardio-warmup',
    name: 'Cardio Warm-Up',
    description: 'Dynamic warm-up for running, rowing, or general cardio sessions',
    workoutType: 'cardio',
    blockType: 'warm-up',
    block: {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        {
          exerciseName: 'Jumping Jacks',
          sets: 1,
          reps: '30',
          notes: 'Full range of motion',
          rpe: 4
        },
        {
          exerciseName: 'Leg Swings',
          sets: 1,
          reps: '10 each leg',
          notes: 'Front to back, then side to side',
          rpe: 3
        },
        {
          exerciseName: 'High Knees',
          sets: 1,
          reps: '30 seconds',
          notes: 'Quick pace, 90-degree knee angle',
          rpe: 5
        },
        {
          exerciseName: 'Walking Lunges',
          sets: 1,
          reps: '10 each leg',
          notes: 'Controlled, deep stretch',
          rpe: 4
        },
        {
          exerciseName: 'Arm Circles',
          sets: 1,
          reps: '15 each direction',
          notes: 'Small to large circles',
          rpe: 3
        }
      ]
    }
  },
  
  // RUCK WARMUP
  {
    id: 'ruck-warmup',
    name: 'Ruck Warm-Up',
    description: 'Pre-ruck mobility and activation for loaded movement',
    workoutType: 'ruck',
    blockType: 'warm-up',
    block: {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        {
          exerciseName: 'World\'s Greatest Stretch',
          sets: 1,
          reps: '5 each side',
          notes: 'Full sequence: lunge, rotation, hamstring stretch',
          rpe: 4
        },
        {
          exerciseName: 'Hip Circles',
          sets: 1,
          reps: '10 each direction',
          notes: 'Large circles, standing',
          rpe: 3
        },
        {
          exerciseName: 'Ankle Circles',
          sets: 1,
          reps: '10 each direction',
          notes: 'Both ankles',
          rpe: 2
        },
        {
          exerciseName: 'Shoulder Dislocates',
          sets: 1,
          reps: '10',
          notes: 'With band or stick, wide grip',
          rpe: 4
        },
        {
          exerciseName: 'Bodyweight Squats',
          sets: 1,
          reps: '15',
          notes: 'Full depth, controlled',
          rpe: 3
        },
        {
          exerciseName: 'Light Ruck',
          sets: 1,
          reps: '5 min',
          notes: 'Easy pace with pack, no weight or light weight',
          rpe: 4
        }
      ]
    }
  },
  
  // STRENGTH-UPPER WARMUP
  {
    id: 'strength-upper-warmup',
    name: 'Upper Body Strength Warm-Up',
    description: 'Shoulder and thoracic mobility for pressing/pulling',
    workoutType: 'strength-upper',
    blockType: 'warm-up',
    block: {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        {
          exerciseName: 'Band Pull-Aparts',
          sets: 2,
          reps: '15',
          notes: 'Light band, chest height',
          rpe: 3
        },
        {
          exerciseName: 'Scapular Push-Ups',
          sets: 2,
          reps: '10',
          notes: 'Protraction/retraction only',
          rpe: 3
        },
        {
          exerciseName: 'Dead Hangs',
          sets: 1,
          reps: '30 sec',
          notes: 'Passive hang, shoulder engagement',
          rpe: 4
        },
        {
          exerciseName: 'Band Dislocates',
          sets: 1,
          reps: '12',
          notes: 'Wide grip, slow and controlled',
          rpe: 3
        },
        {
          exerciseName: 'Cat-Cow Stretch',
          sets: 1,
          reps: '10',
          notes: 'Thoracic mobility focus',
          rpe: 3
        },
        {
          exerciseName: 'Light Barbell/Dumbbell Warm-Up',
          sets: 2,
          reps: '10',
          notes: 'Main lift movement at 40-50% working weight',
          rpe: 4
        }
      ]
    }
  },
  
  // STRENGTH-LOWER WARMUP
  {
    id: 'strength-lower-warmup',
    name: 'Lower Body Strength Warm-Up',
    description: 'Hip and ankle mobility for squatting/deadlifting',
    workoutType: 'strength-lower',
    blockType: 'warm-up',
    block: {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        {
          exerciseName: 'Glute Bridges',
          sets: 2,
          reps: '15',
          notes: 'Bodyweight, full squeeze at top',
          rpe: 3
        },
        {
          exerciseName: 'Cossack Squats',
          sets: 1,
          reps: '8 each side',
          notes: 'Bodyweight, slow tempo',
          rpe: 4
        },
        {
          exerciseName: '90/90 Hip Stretch',
          sets: 1,
          reps: '30 sec each side',
          notes: 'Internal and external rotation',
          rpe: 4
        },
        {
          exerciseName: 'Ankle Dorsiflexion',
          sets: 1,
          reps: '10 each leg',
          notes: 'Knee to wall, hold 2 seconds',
          rpe: 3
        },
        {
          exerciseName: 'Goblet Squats',
          sets: 2,
          reps: '10',
          notes: 'Light weight, ATG depth',
          rpe: 4
        },
        {
          exerciseName: 'Light Barbell Warm-Up',
          sets: 2,
          reps: '8',
          notes: 'Main lift movement at 40-60% working weight',
          rpe: 5
        }
      ]
    }
  },
  
  // GENERAL STRENGTH WARMUP
  {
    id: 'strength-general-warmup',
    name: 'General Strength Warm-Up',
    description: 'Full-body mobility and activation for mixed strength work',
    workoutType: 'strength',
    blockType: 'warm-up',
    block: {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        {
          exerciseName: 'Jumping Jacks',
          sets: 1,
          reps: '30',
          notes: 'Elevate heart rate',
          rpe: 4
        },
        {
          exerciseName: 'Inchworms',
          sets: 1,
          reps: '8',
          notes: 'Walk hands out to push-up, walk feet to hands',
          rpe: 4
        },
        {
          exerciseName: 'Bodyweight Squats',
          sets: 1,
          reps: '15',
          notes: 'Deep, controlled',
          rpe: 3
        },
        {
          exerciseName: 'Push-Ups',
          sets: 1,
          reps: '10',
          notes: 'Controlled tempo',
          rpe: 4
        },
        {
          exerciseName: 'Spiderman Lunges',
          sets: 1,
          reps: '6 each side',
          notes: 'Hip opener, reach elbow to ankle',
          rpe: 4
        },
        {
          exerciseName: 'Shoulder Dislocates',
          sets: 1,
          reps: '10',
          notes: 'Band or PVC pipe',
          rpe: 3
        }
      ]
    }
  },
  
  // SWIM WARMUP
  {
    id: 'swim-warmup',
    name: 'Swim Warm-Up',
    description: 'Pool-side mobility and easy swimming to prep for main set',
    workoutType: 'swim',
    blockType: 'warm-up',
    block: {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        {
          exerciseName: 'Arm Swings',
          sets: 1,
          reps: '15 each arm',
          notes: 'Large circles, forward and backward',
          rpe: 3
        },
        {
          exerciseName: 'Trunk Rotations',
          sets: 1,
          reps: '15 each direction',
          notes: 'Standing, arms extended',
          rpe: 3
        },
        {
          exerciseName: 'Leg Swings',
          sets: 1,
          reps: '10 each leg',
          notes: 'Front/back and lateral',
          rpe: 3
        },
        {
          exerciseName: 'Easy Swim',
          sets: 1,
          reps: '200m',
          notes: 'Choice stroke, relaxed pace',
          rpe: 4
        },
        {
          exerciseName: 'Swim Drills',
          sets: 1,
          reps: '100m',
          notes: 'Catch-up drill, fingertip drag, or kickboard',
          rpe: 4
        },
        {
          exerciseName: 'Build Swim',
          sets: 1,
          reps: '50m x 2',
          notes: 'Start easy, finish at 70%',
          rpe: 6
        }
      ]
    }
  }
];

export const COOLDOWN_TEMPLATES: BlockTemplate[] = [
  // CARDIO COOL-DOWN
  {
    id: 'cardio-cooldown',
    name: 'Cardio Cool-Down',
    description: 'Active recovery and static stretching post-cardio',
    workoutType: 'cardio',
    blockType: 'cool-down',
    block: {
      name: 'Cool Down',
      type: 'recovery',
      exercises: [
        {
          exerciseName: 'Easy Jog or Walk',
          sets: 1,
          reps: '3-5 min',
          notes: 'Bring heart rate down gradually',
          rpe: 3
        },
        {
          exerciseName: 'Standing Quad Stretch',
          sets: 1,
          reps: '30 sec each leg',
          notes: 'Pull heel to glute, knee down',
          rpe: 3
        },
        {
          exerciseName: 'Standing Hamstring Stretch',
          sets: 1,
          reps: '30 sec each leg',
          notes: 'Leg elevated, hinge at hip',
          rpe: 3
        },
        {
          exerciseName: 'Calf Stretch',
          sets: 1,
          reps: '30 sec each leg',
          notes: 'Wall stretch, heel down',
          rpe: 3
        },
        {
          exerciseName: 'Hip Flexor Stretch',
          sets: 1,
          reps: '30 sec each side',
          notes: 'Kneeling lunge, posterior tilt',
          rpe: 4
        },
        {
          exerciseName: 'Child\'s Pose',
          sets: 1,
          reps: '45 sec',
          notes: 'Deep breathing, full relaxation',
          rpe: 2
        }
      ]
    }
  },
  
  // RUCK COOL-DOWN
  {
    id: 'ruck-cooldown',
    name: 'Ruck Cool-Down',
    description: 'Post-ruck recovery for feet, legs, and lower back',
    workoutType: 'ruck',
    blockType: 'cool-down',
    block: {
      name: 'Cool Down',
      type: 'recovery',
      exercises: [
        {
          exerciseName: 'Easy Walk',
          sets: 1,
          reps: '5 min',
          notes: 'No pack, slow pace',
          rpe: 2
        },
        {
          exerciseName: 'Figure-4 Stretch',
          sets: 1,
          reps: '45 sec each side',
          notes: 'Lying or seated piriformis stretch',
          rpe: 4
        },
        {
          exerciseName: 'Prone Scorpion',
          sets: 1,
          reps: '8 each side',
          notes: 'Thoracic rotation and hip stretch',
          rpe: 3
        },
        {
          exerciseName: 'Seated Forward Fold',
          sets: 1,
          reps: '60 sec',
          notes: 'Hamstrings and lower back',
          rpe: 4
        },
        {
          exerciseName: 'Ankle Circles',
          sets: 1,
          reps: '15 each direction',
          notes: 'Both ankles, full range',
          rpe: 2
        },
        {
          exerciseName: 'Supine Twist',
          sets: 1,
          reps: '45 sec each side',
          notes: 'Knees to chest, drop to side',
          rpe: 3
        }
      ]
    }
  },
  
  // STRENGTH-UPPER COOL-DOWN
  {
    id: 'strength-upper-cooldown',
    name: 'Upper Body Strength Cool-Down',
    description: 'Post-workout stretching for chest, back, and shoulders',
    workoutType: 'strength-upper',
    blockType: 'cool-down',
    block: {
      name: 'Cool Down',
      type: 'recovery',
      exercises: [
        {
          exerciseName: 'Doorway Chest Stretch',
          sets: 1,
          reps: '45 sec each side',
          notes: 'High, mid, and low positions',
          rpe: 4
        },
        {
          exerciseName: 'Cross-Body Shoulder Stretch',
          sets: 1,
          reps: '30 sec each arm',
          notes: 'Pull arm across chest',
          rpe: 3
        },
        {
          exerciseName: 'Overhead Tricep Stretch',
          sets: 1,
          reps: '30 sec each arm',
          notes: 'Elbow behind head, gentle pull',
          rpe: 3
        },
        {
          exerciseName: 'Child\'s Pose with Rotation',
          sets: 1,
          reps: '45 sec each side',
          notes: 'Thread the needle stretch',
          rpe: 3
        },
        {
          exerciseName: 'Lying Pec Stretch',
          sets: 1,
          reps: '60 sec',
          notes: 'Arms out in T position',
          rpe: 4
        },
        {
          exerciseName: 'Dead Hang',
          sets: 1,
          reps: '30-60 sec',
          notes: 'Passive hang, decompress spine',
          rpe: 3
        }
      ]
    }
  },
  
  // STRENGTH-LOWER COOL-DOWN
  {
    id: 'strength-lower-cooldown',
    name: 'Lower Body Strength Cool-Down',
    description: 'Post-squat/deadlift recovery and flexibility',
    workoutType: 'strength-lower',
    blockType: 'cool-down',
    block: {
      name: 'Cool Down',
      type: 'recovery',
      exercises: [
        {
          exerciseName: 'Walking',
          sets: 1,
          reps: '3-5 min',
          notes: 'Easy pace, flush out legs',
          rpe: 2
        },
        {
          exerciseName: 'Couch Stretch',
          sets: 1,
          reps: '90 sec each leg',
          notes: 'Deep hip flexor stretch',
          rpe: 5
        },
        {
          exerciseName: 'Pigeon Pose',
          sets: 1,
          reps: '90 sec each side',
          notes: 'Hip external rotation and glute stretch',
          rpe: 4
        },
        {
          exerciseName: 'Standing Hamstring Stretch',
          sets: 1,
          reps: '45 sec each leg',
          notes: 'Leg on box, hinge at hip',
          rpe: 4
        },
        {
          exerciseName: 'Supine Figure-4',
          sets: 1,
          reps: '60 sec each side',
          notes: 'Lying piriformis stretch',
          rpe: 4
        },
        {
          exerciseName: 'Spinal Twist',
          sets: 1,
          reps: '60 sec each side',
          notes: 'Supine, knees dropped, decompress lower back',
          rpe: 3
        }
      ]
    }
  },
  
  // GENERAL STRENGTH COOL-DOWN
  {
    id: 'strength-general-cooldown',
    name: 'General Strength Cool-Down',
    description: 'Full-body stretching and recovery',
    workoutType: 'strength',
    blockType: 'cool-down',
    block: {
      name: 'Cool Down',
      type: 'recovery',
      exercises: [
        {
          exerciseName: 'Light Movement',
          sets: 1,
          reps: '3-5 min',
          notes: 'Walk, bike, or row at easy pace',
          rpe: 3
        },
        {
          exerciseName: 'World\'s Greatest Stretch',
          sets: 1,
          reps: '3 each side',
          notes: 'Slow and controlled',
          rpe: 4
        },
        {
          exerciseName: 'Cat-Cow',
          sets: 1,
          reps: '10 reps',
          notes: 'Spinal mobility',
          rpe: 3
        },
        {
          exerciseName: 'Child\'s Pose',
          sets: 1,
          reps: '60 sec',
          notes: 'Deep breathing',
          rpe: 2
        },
        {
          exerciseName: 'Hip Flexor Stretch',
          sets: 1,
          reps: '45 sec each side',
          notes: 'Kneeling or standing',
          rpe: 4
        },
        {
          exerciseName: 'Seated Forward Fold',
          sets: 1,
          reps: '60 sec',
          notes: 'Hamstrings and back',
          rpe: 3
        }
      ]
    }
  },
  
  // SWIM COOL-DOWN
  {
    id: 'swim-cooldown',
    name: 'Swim Cool-Down',
    description: 'Easy swimming and shoulder recovery',
    workoutType: 'swim',
    blockType: 'cool-down',
    block: {
      name: 'Cool Down',
      type: 'recovery',
      exercises: [
        {
          exerciseName: 'Easy Swim',
          sets: 1,
          reps: '200m',
          notes: 'Choice stroke, very relaxed',
          rpe: 3
        },
        {
          exerciseName: 'Backstroke',
          sets: 1,
          reps: '100m',
          notes: 'Easy pace, open up shoulders',
          rpe: 3
        },
        {
          exerciseName: 'Cross-Body Shoulder Stretch',
          sets: 1,
          reps: '30 sec each arm',
          notes: 'Pool-side or in water',
          rpe: 3
        },
        {
          exerciseName: 'Overhead Reach',
          sets: 1,
          reps: '30 sec each arm',
          notes: 'Triceps and lat stretch',
          rpe: 3
        },
        {
          exerciseName: 'Doorway Chest Stretch',
          sets: 1,
          reps: '45 sec each side',
          notes: 'Multiple angles',
          rpe: 4
        },
        {
          exerciseName: 'Neck Stretches',
          sets: 1,
          reps: '20 sec each direction',
          notes: 'Gentle, all four directions',
          rpe: 3
        }
      ]
    }
  }
];

// Combined export for all templates
export const ALL_BLOCK_TEMPLATES = [...WARMUP_TEMPLATES, ...COOLDOWN_TEMPLATES];

// Helper functions
export function getTemplateById(id: string): BlockTemplate | undefined {
  return ALL_BLOCK_TEMPLATES.find(t => t.id === id);
}

export function getTemplatesByWorkoutType(workoutType: BlockTemplate['workoutType']): BlockTemplate[] {
  return ALL_BLOCK_TEMPLATES.filter(t => t.workoutType === workoutType);
}

export function getWarmupTemplates(): BlockTemplate[] {
  return WARMUP_TEMPLATES;
}

export function getCooldownTemplates(): BlockTemplate[] {
  return COOLDOWN_TEMPLATES;
}

